if (!customElements.get('lv-customer-reviews')) {
  customElements.define('lv-customer-reviews', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.shown = 0;
      this.size = 6;
      this.resize = new ResizeObserver(entries => {
        for (const entry of entries) {
          const card = entry.target;
          card.style.gridRowEnd = `span ${Math.ceil((card.getBoundingClientRect().height + 24) / 8)}`;
        }
      });
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) { this.observer.disconnect(); this.load(); }
      }, {rootMargin: '400px'});
      this.observer.observe(this);
    }
    disconnectedCallback() { this.observer?.disconnect(); this.resize?.disconnect(); }
    async load() {
      const status = this.querySelector('[data-review-status]');
      try {
        let data = {reviews: [], count: 0, average: 0, label: ''};
        if (this.dataset.showExisting !== 'false') {
          const reviews = await this.loadLibrary();
          data.label = this.dataset.label || '';
          data.reviews = reviews.filter(review => review.visible !== false && (
            this.dataset.scope === 'all' ? review.home === true :
            review.product ? String(review.product) === this.dataset.productId : review.range === data.label
          ));
        }
        const added = JSON.parse(this.querySelector('[data-custom-reviews]')?.textContent || '[]');
        this.reviews = [...added, ...data.reviews];
        data.count = this.reviews.length;
        data.average = data.count ? this.reviews.reduce((sum, review) => sum + Number(review.rating), 0) / data.count : 0;
        this.shown = 0;
        this.querySelector('[data-review-grid]').replaceChildren();
        this.querySelector('[data-review-summary]').textContent = `${data.average.toLocaleString('fr-FR', {minimumFractionDigits:2,maximumFractionDigits:2})}/5 · ${data.count.toLocaleString('fr-FR')} avis clients`;
        this.querySelector('[data-review-subtitle]').textContent = this.dataset.scope === 'all' ? 'Des expériences partagées sur les différentes gammes LOMVOR.' : (data.label ? `${data.label} · Avis de la gamme, tous coloris confondus.` : 'Les expériences de nos clients.');
        this.appendReviews(false);
      } catch (_) {
        status.textContent = 'Les avis ne sont pas disponibles pour le moment. ';
        const retry = this.element('button', 'Réessayer');
        retry.type = 'button'; retry.onclick = () => { status.textContent = 'Chargement des avis…'; this.load(); };
        status.append(retry);
      }
    }
    async loadLibrary() {
      const library = new URL(this.dataset.library, location.origin);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) library.searchParams.set('preview_theme_id', preview);
      const cache = window.lomvorReviewLibraryCache ||= new Map();
      const key = library.href;
      if (!cache.has(key)) {
        const pending = (async () => {
          const reviews = [];
          const visited = new Set();
          let url = library;
          let expected;
          while (url) {
            if (visited.has(url.href) || visited.size >= 100) throw new Error('Review pagination loop');
            visited.add(url.href);
            const response = await fetch(url.href);
            if (!response.ok) throw new Error('Review library unavailable');
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const node = doc.querySelector('[data-lomvor-review-library]');
            if (!node) throw new Error('Review library missing');
            const page = JSON.parse(node.textContent);
            if (!Array.isArray(page.reviews)) throw new Error('Invalid review library');
            expected ??= page.total;
            reviews.push(...page.reviews);
            url = page.next ? new URL(page.next, library) : null;
            if (url) {
              if (url.origin !== location.origin) throw new Error('Invalid pagination origin');
              url.searchParams.set('section_id', 'lomvor-review-library');
              if (preview) url.searchParams.set('preview_theme_id', preview);
            }
          }
          const unique = [...new Map(reviews.map(review => [review.id, review])).values()];
          if (unique.length !== expected) throw new Error('Incomplete review library');
          return unique.sort((a, b) => Number(a.position) - Number(b.position) || a.id.localeCompare(b.id));
        })();
        cache.set(key, pending);
        pending.catch(() => cache.delete(key));
      }
      return cache.get(key);
    }
    element(tag, text, className) {
      const el = document.createElement(tag);
      if (text !== undefined) el.textContent = text;
      if (className) el.className = className;
      return el;
    }
    appendReviews(focus) {
      const grid = this.querySelector('[data-review-grid]');
      const start = this.shown;
      const end = Math.min(start + this.size, this.reviews.length);
      let first;
      for (const review of this.reviews.slice(start, end)) {
        const card = this.element('article', undefined, 'lv-review-card');
        card.tabIndex = -1;
        card.dataset.reviewId = review.id;
        const marker = [...this.querySelectorAll('[data-review-block-id]')].find(el => el.dataset.reviewBlockId === review.id);
        if (marker) for (const attr of marker.attributes) if (attr.name.startsWith('data-shopify-')) card.setAttribute(attr.name, attr.value);
        if (!first) first = card;
        if (review.image) {
          const photo = this.element('img');
          const url = new URL(review.image, location.origin); url.searchParams.set('width', '720');
          photo.src = url.href; photo.alt = `Photo jointe à l’avis sur ${review.range}`;
          photo.loading = 'lazy'; photo.decoding = 'async'; photo.draggable = false;
          photo.addEventListener('error', () => photo.remove(), {once:true});
          card.append(photo);
        }
        const body = this.element('div', undefined, 'lv-review-card__body');
        const stars = this.element('span', '★'.repeat(review.rating) + '☆'.repeat(5-review.rating), 'lv-review-card__stars');
        stars.setAttribute('aria-label', `${review.rating} sur 5`);
        const text = this.element('p', review.text, 'lv-review-card__text');
        body.append(stars, text);
        if (review.text.length > 185) {
          const short = review.text.slice(0,185).replace(/\s+\S*$/, '') + '…';
          text.textContent = short;
          const more = this.element('button','Lire la suite','lv-review-card__more');
          more.type = 'button'; more.setAttribute('aria-expanded','false');
          more.setAttribute('aria-label',`Lire la suite de l’avis de ${review.name}`);
          more.onclick = () => {
            const expanded = more.getAttribute('aria-expanded') !== 'true';
            text.textContent = expanded ? review.text : short;
            more.textContent = expanded ? 'Réduire' : 'Lire la suite';
            more.setAttribute('aria-label',`${expanded ? 'Réduire' : 'Lire la suite de'} l’avis de ${review.name}`);
            more.setAttribute('aria-expanded',String(expanded));
          };
          body.append(more);
        }
        body.append(this.element('strong', review.name), this.element('span', review.range, 'lv-review-card__range'));
        card.append(body); grid.append(card); this.resize.observe(card);
        card.style.gridRowEnd = `span ${Math.ceil((card.getBoundingClientRect().height + 24) / 8)}`;
      }
      this.shown = end;
      const nav = this.querySelector('[data-review-pagination]');
      let button = nav.querySelector('button');
      if (end < this.reviews.length) {
        if (!button) {
          button = this.element('button', 'Voir plus d’avis'); button.type = 'button';
          button.onclick = () => this.appendReviews(true);
          nav.replaceChildren(button);
        }
      } else nav.replaceChildren();
      this.querySelector('[data-review-status]').textContent = `${end.toLocaleString('fr-FR')} avis affichés sur ${this.reviews.length.toLocaleString('fr-FR')}`;
      if (focus && first) first.focus({preventScroll:true});
    }
  });
}

